import React, { useRef, useEffect, useState, useMemo } from 'react';
import MultiSelectDropdown from '../MultiSelectDropdown';

const formatAnnotation = (annotation) => {
  // Replace numbers following ': ' with white-colored spans
  return annotation.replace(/: (-?\d+\.?\d*)/g, ': <span style="color: #fff">$1</span>');
};

const filterLogs = (logs, filters, logData) => {
  let filteredLogs = logs;

  if (filters.selectedCanId.length > 0) {
    filteredLogs = filteredLogs.filter(log => filters.selectedCanId.includes(log.canId));
  }

  if (filters.selectedFlags.length > 0) {
    filteredLogs = filteredLogs.filter(log => {
      if (!log.individualFlags) return false;
      return filters.selectedFlags.some(selectedFlag => log.individualFlags.includes(selectedFlag));
    });
  }

  if (filters.selectedClgFiles.length > 0) {
    const sourceTextToFilename = {};
    if (logData.clgSources) {
      logData.clgSources.forEach(source => {
        sourceTextToFilename[source.sourceText || source.filename] = source.filename;
      });
    }
    const selectedFilenames = filters.selectedClgFiles.map(sourceText => sourceTextToFilename[sourceText] || sourceText);
    filteredLogs = filteredLogs.filter(log => log.source && selectedFilenames.includes(log.source));
  }

  if (filters.annotationFilter.trim()) {
    filteredLogs = filteredLogs.filter(log => 
      log.annotation && log.annotation.toLowerCase().includes(filters.annotationFilter.toLowerCase())
    );
  }

  if (filters.showOnlyAnnotated) {
    filteredLogs = filteredLogs.filter(log => log.annotation && log.annotation.trim());
  }

  return filteredLogs;
};

const LogViewer = ({ 
  logData,
  dbLogData,
  canIds,
  currentVideoTime,
  loadingChunks,
  dbcFiles,
  selectedDbcFile,
  onDbcFileChange
}) => {
  const clgLogRef = useRef(null);
  const dbLogRef = useRef(null);
  const [clgAutoScroll, setClgAutoScroll] = useState(true);
  const [dbAutoScroll, setDbAutoScroll] = useState(true);
  const [selectedCanId, setSelectedCanId] = useState([]);
  const [selectedFlags, setSelectedFlags] = useState([]);
  const [selectedClgFiles, setSelectedClgFiles] = useState([]);
  const [annotationFilter, setAnnotationFilter] = useState('');
  const [showHexData, setShowHexData] = useState(true);
  const [showOnlyAnnotated, setShowOnlyAnnotated] = useState(false);
  const [clgFilesDropdownOpen, setClgFilesDropdownOpen] = useState(false);
  const [canIdDropdownOpen, setCanIdDropdownOpen] = useState(false);
  const [flagsDropdownOpen, setFlagsDropdownOpen] = useState(false);

  // Memoize options to prevent recalculation on every render
  const clgFileOptions = useMemo(() => {
    return logData?.clgSources ? 
      logData.clgSources.map(source => source.sourceText || source.filename) :
      [...new Set(logData?.logs?.map(log => log.source).filter(Boolean) || [])].sort();
  }, [logData?.clgSources, logData?.logs]);

  const canIdOptions = useMemo(() => {
    return canIds ? canIds.map(canId => `${canId.id} - ${canId.name}`) : [];
  }, [canIds]);

  // Create mapping for selected values display
  const selectedCanIdOptions = useMemo(() => {
    if (!canIds || !selectedCanId.length) return [];
    console.log(canIds, selectedCanId, canIdOptions)
    return canIdOptions.filter(option => {
      const id = option.split(' - ')[0];
      return selectedCanId.includes(parseInt(id, 10));
    });
  }, [canIds, canIdOptions, selectedCanId]);

  const flagOptions = useMemo(() => {
    return (logData?.logs || []).reduce((flags, log) => {
      if (log.individualFlags) {
        log.individualFlags.forEach(flag => {
          if (!flags.includes(flag)) flags.push(flag);
        });
      }
      return flags;
    }, []).sort();
  }, [logData?.logs]);

  const filters = useMemo(() => ({
    selectedCanId,
    selectedFlags,
    selectedClgFiles,
    annotationFilter,
    showOnlyAnnotated
  }), [selectedCanId, selectedFlags, selectedClgFiles, annotationFilter, showOnlyAnnotated]);

  const currentLogs = useMemo(() => {
    if (!logData?.logs) return [];
    const currentSecond = Math.floor(currentVideoTime);
    const displayLogs = logData.logs.filter(log => 
      Math.floor(log.timestamp) === currentSecond
    );
    const filtered = filterLogs(displayLogs, filters, logData);
    return filtered.slice(0, 5000);
  }, [logData, currentVideoTime, filters]);

  const currentDbLogs = useMemo(() => {
    if (!dbLogData?.logs) return [];
    return dbLogData.logs.filter(log => log.timestamp <= currentVideoTime);
  }, [dbLogData, currentVideoTime]);

  // Auto-scroll logs
  useEffect(() => {
    if (clgLogRef.current && clgAutoScroll) {
      clgLogRef.current.scrollTop = clgLogRef.current.scrollHeight;
    }
  }, [currentLogs, clgAutoScroll]);

  useEffect(() => {
    if (dbLogRef.current && dbAutoScroll) {
      dbLogRef.current.scrollTop = dbLogRef.current.scrollHeight;
    }
  }, [currentDbLogs, dbAutoScroll]);

  const handleClgScroll = () => {
    if (clgLogRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = clgLogRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
      setClgAutoScroll(isAtBottom);
    }
  };

  const handleDbScroll = () => {
    if (dbLogRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = dbLogRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
      setDbAutoScroll(isAtBottom);
    }
  };

  const toggleCanId = (formattedCanId) => {
    const canId = parseInt(formattedCanId.split(' - ')[0], 10); // Extract just the ID
    setSelectedCanId(prev => 
      prev.includes(canId) 
        ? prev.filter(id => id !== canId)
        : [...prev, canId]
    );
  };

  const toggleAllCanIds = () => {
    const allCanIds = canIds ? canIds.map(canId => canId.id) : [];
    const hasAllSelected = allCanIds.every(canId => selectedCanId.includes(canId));
    if (hasAllSelected) {
      setSelectedCanId([]);
    } else {
      setSelectedCanId(allCanIds);
    }
  };

  const toggleFlags = (flags) => {
    setSelectedFlags(prev => 
      prev.includes(flags) 
        ? prev.filter(f => f !== flags)
        : [...prev, flags]
    );
  };

  const toggleAllFlags = () => {
    const hasAllSelected = flagOptions.every(flag => selectedFlags.includes(flag));
    if (hasAllSelected) {
      setSelectedFlags([]);
    } else {
      setSelectedFlags(flagOptions);
    }
  };

  const toggleClgFile = (clgFile) => {
    setSelectedClgFiles(prev => 
      prev.includes(clgFile) 
        ? prev.filter(f => f !== clgFile)
        : [...prev, clgFile]
    );
  };

  const toggleAllClgFiles = () => {
    const hasAllSelected = clgFileOptions.every(file => selectedClgFiles.includes(file));
    if (hasAllSelected) {
      setSelectedClgFiles([]);
    } else {
      setSelectedClgFiles(clgFileOptions);
    }
  };

  return (
    <div className="logs-container">
      {logData && logData.logs && (
        <div className="log-viewer clg-logs">
          <h5 style={{ margin: 0, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            CLG Messages ({currentLogs.length} displayed)
            {loadingChunks && (
              <>
                <div style={{ width: '12px', height: '12px', border: '2px solid #ccc', borderTop: '2px solid #666', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
                <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal' }}>Loading logs...</span>
              </>
            )}
            <span style={{ fontSize: '10px', color: '#999' }}>
              Total logs: {logData?.logs?.length || 0}, Selected CAN IDs: [{selectedCanId.join(', ')}]
            </span>
          </h5>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            DBC File:
            {dbcFiles && dbcFiles.length > 1 && (
              <select
                value={selectedDbcFile || ''}
                onChange={(e) => onDbcFileChange(e.target.value)}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  borderRadius: '3px',
                  border: '1px solid #ccc',
                  background: '#fff'
                }}
              >
                {dbcFiles.map(file => (
                  <option key={file} value={file}>{file}</option>
                ))}
              </select>
            )}
            <MultiSelectDropdown
              key="clg-files"
              options={clgFileOptions}
              selectedValues={selectedClgFiles}
              onToggle={toggleClgFile}
              onToggleAll={toggleAllClgFiles}
              isOpen={clgFilesDropdownOpen}
              onToggleOpen={setClgFilesDropdownOpen}
              label="CLG Files"
            />
            <MultiSelectDropdown
              key="can-ids"
              options={canIdOptions}
              selectedValues={selectedCanIdOptions}
              onToggle={toggleCanId}
              onToggleAll={toggleAllCanIds}
              isOpen={canIdDropdownOpen}
              onToggleOpen={setCanIdDropdownOpen}
              label="CAN IDs"
            />
            <MultiSelectDropdown
              key="flags"
              options={flagOptions}
              selectedValues={selectedFlags}
              onToggle={toggleFlags}
              onToggleAll={toggleAllFlags}
              isOpen={flagsDropdownOpen}
              onToggleOpen={setFlagsDropdownOpen}
              label="Flags"
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
              <input
                type="checkbox"
                checked={showHexData}
                onChange={(e) => setShowHexData(e.target.checked)}
                style={{ margin: 0 }}
              />
              Hex Data
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
              <input
                type="checkbox"
                checked={showOnlyAnnotated}
                onChange={(e) => setShowOnlyAnnotated(e.target.checked)}
                style={{ margin: 0 }}
              />
              Only Signals
            </label>
            <input
              type="text"
              placeholder="Filter signals..."
              value={annotationFilter}
              onChange={(e) => setAnnotationFilter(e.target.value)}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                borderRadius: '3px',
                border: '1px solid #ccc',
                minWidth: '120px'
              }}
            />
          </div>
          <div ref={clgLogRef} onScroll={handleClgScroll} className="log-content">
            {logData?.logs?.length > 0 && currentLogs.length === 0 && (
              <div style={{ padding: '10px', color: '#666', fontSize: '11px' }}>
                No logs match current filters.<br/>
              </div>
            )}
            {currentLogs.map((log, index) => (
              <div key={index} className="log-entry">
                <span className="log-timestamp">{(log.appTimestamp || 0).toFixed(6)}s</span>
                <span className="log-canid">{log.canId}</span>
                {log.flags && <span className="log-flags">[{log.flags}]</span>}
                {showHexData && <span className="log-data">{log.data}</span>}
                {log.annotation && <span className="log-annotation" dangerouslySetInnerHTML={{ __html: formatAnnotation(log.annotation) }} />}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {dbLogData && (
        <div className="log-viewer db-logs">
          <h5>DB Messages ({currentDbLogs.length} displayed)</h5>
          <div ref={dbLogRef} onScroll={handleDbScroll} className="log-content">
            {currentDbLogs.map((log, index) => {
              const dbTime = typeof log.timestamp === 'number' ? log.timestamp.toFixed(3) : log.timestamp;
              return (
                <div key={index} className="log-entry">
                  <span className="log-timestamp">DB:{dbTime}s</span>
                  <span className="log-canid">{log.canId}</span>
                  <span className="log-data">{log.data}</span>
                  {log.annotation && <span className="log-annotation" dangerouslySetInnerHTML={{ __html: formatAnnotation(log.annotation) }} />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LogViewer;